import { useEffect, useRef, useState } from 'react';
import { useSearchSearchCandidateProfileQueriesQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useUser } from 'core/stores/hooks/useUser';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

type UseHasSearchCandidateProfileQueryHistoryProps = {
  conversationHistoryLength: number;
};

export const useHasSearchCandidateProfileQueryHistory = ({
  conversationHistoryLength
}: UseHasSearchCandidateProfileQueryHistoryProps) => {
  const user = useUser();
  const organization = useOrganization();
  const previousHistoryLength = useRef(conversationHistoryLength);
  // OpenSearch lags the archive by a few seconds, so a refetch straight after
  // a delete still returns the deleted search. Exclude it by ID instead.
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const { data, previousData, loading, refetch } =
    useSearchSearchCandidateProfileQueriesQuery({
      variables: {
        limit: 1,
        filter: {
          userId: { eq: user.id },
          organizationId: { eq: organization.id },
          isArchived: { ne: true },
          ...(deletedIds.length > 0 && {
            and: deletedIds.map((id) => ({ id: { ne: id } }))
          })
        }
      },
      fetchPolicy: 'cache-and-network'
    });

  // Refetch when user creates their first conversation
  useEffect(() => {
    if (previousHistoryLength.current === 0 && conversationHistoryLength > 0) {
      refetch();
    }
    previousHistoryLength.current = conversationHistoryLength;
  }, [conversationHistoryLength, refetch]);

  // previousData keeps the link steady while the new filter loads.
  const hasHistory =
    (extractQueryListItems(data ?? previousData)?.length ?? 0) > 0;

  const excludeDeleted = (id: string) => setDeletedIds((ids) => [...ids, id]);

  return {
    hasHistory,
    loading,
    excludeDeleted
  };
};
